import { NetworkStatus, useLazyQuery, useMutation, useSubscription } from '@apollo/client';
import {
  AllForemanId,
  AllGeneralForemanId,
  AllSuperintendentId,
  DND_IDENTIFIER,
  ErrorRetry,
  PlanningActionType,
  TOAST_ID_SELECT_FOREMAN,
  UnassignedForemanId,
  UnassignedGeneralForemanId,
  UnassignedSuperintendentId,
  deleteApproveClaimedMilestone,
  getSavePlanningOptimisticResponse,
  getUnplanOptimisticResponse,
  splitIdentifiers,
  updateCache,
  updateUnplanCache,
  useAppUser,
  useConfig,
  useDebounce,
  usePlanning,
} from '@bechtel/client-common';
import {
  EXPIRED_MILESTONES_COUNT,
  GET_PLANNED_MILESTONES,
  GET_WORKPACKAGE_ADDITIONAL_INFO_BY_IDS,
  SAVE_PLAN,
  SUBSCRIPTION_MILESTONES_UNPLANNED,
  SUBSCRIPTION_PLANNING_AI_COMPLETED,
  SUBSCRIPTION_PLANNING_WEEK_SAVED,
  UNPLAN,
} from '@bechtel/client-gql';
import { WorkPackageDetailHeader } from '@bechtel/client-work-packages';
import {
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Icon,
  IconSize,
  Modal,
  ModalClose,
  ModalSize,
  Spinner,
  SpinnerSize,
  Text,
  TextArea,
  TextFieldSize,
  ToastAction,
  ToastType,
  VisuallyHidden,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  CommodityMilestone,
  CrewMemberExpiredMilestoneStats,
  PersonaType,
  PlanningCrewMemberInput,
  PlanningError,
  PlanningFilterInput,
  PlanningPayload,
  PlanningWeek,
  PlanningWeekStats,
  SavePlanningInput,
  UnplanInput,
  WorkPackage,
} from '@bechtel/types';
import { Trans, t } from '@lingui/macro';
import { assign, filter, find, forEach, get, includes, isEmpty, lowerCase, map, noop, nth, sortBy } from 'lodash';
import React, { Fragment, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, DragStart, Droppable, DroppableProvided, DroppableStateSnapshot } from 'react-beautiful-dnd';
import { usePlanningPermissions } from '~/hooks';
import { AiModalBodyStyled, AiModalFooterStyled, AiModalHeaderStyled } from '~/list/workpackage/styled';
import {
  ColumnContainerStyled,
  PlanningActionContainer,
  PlanningContentContainer,
  WeekColumnDroppableStyled,
  WeekPlanDetailContainer,
  WeekPlanDetailScrollable,
} from '../styled';
import { MilestoneIdentifier, SelectionSection } from '../types';
import { PlanningActions } from './actions';
import { Backlog } from './backlog';
import { useCancelAiPlanningHandler } from './cancel-ai-planning-handler';
import { CommodityCards } from './commodity';
import { getPlanningWeekDataNoMilestones } from './commodity/card/form-data';
import { PlanMilestoneError, SavePlanError } from './error';
import { PlanningWeeksHeader } from './planning-weeks-header';
import { PlanningWeeksFilter } from './planning-weeks-header/types';
import { useSaveAiPlanningHandler } from './save-ai-planning-handler';
import { PlannedMilestoneFooter, PlannedMilestoneFooterActions, PlannedMilestoneFooterText } from './styled';
import { PlanningWeekCard } from './weekplancard';

interface PlannedMilestonesProps {
  setSelectedMilestoneDraggableIds: (value: string[] | []) => void;
  selectedMilestoneDraggableIds: string[];
  draggingMilestoneId: string;
  milestoneIdentifiers: MutableRefObject<Map<string, MilestoneIdentifier>>;
  plannedMilestonesData: MutableRefObject<PlanningWeek[]>;
  planningWeeksFilter: PlanningWeeksFilter;
  backlogRefetch: MutableRefObject<(() => void) | null>;
  weekStatsRefetch: MutableRefObject<(() => void) | null>;
  expiredMilestoneRefetch: MutableRefObject<(() => void) | null>;
  onAiPlannedMilestoneCount?: (count: number, message?: string) => void;
  aiStatusMessageData: any;
  setAiStatusMessageData: (data: any) => void;
  showAiSummaryModal: boolean;
  setShowAiSummaryModal: (show: boolean) => void;
}

// Confirmation Modal Component
interface ConfirmChangeModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (comment: string) => void;
}

// Normalize snake_case statusMessageData from the backend into the camelCase shape the modal expects
const parseStatusMessageData = (raw: any) => {
  if (!raw) return null;
  const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
  return {
    planStatus: data.plan_status ?? data.planStatus,
    capacity: data.capacity
      ? {
          totalCapacityHours: data.capacity.total_capacity_hours ?? data.capacity.totalCapacityHours,
          scheduledHours: data.capacity.scheduled_hours ?? data.capacity.scheduledHours,
          remainingHours: data.capacity.remaining_hours ?? data.capacity.remainingHours,
        }
      : undefined,
    pipeline: data.pipeline
      ? {
          inputMilestones: data.pipeline.input_milestones ?? data.pipeline.inputMilestones,
          eligibleMilestones: data.pipeline.eligible_milestones ?? data.pipeline.eligibleMilestones,
          eligibleHours: data.pipeline.eligible_hours ?? data.pipeline.eligibleHours,
          scheduledMilestones: data.pipeline.scheduled_milestones ?? data.pipeline.scheduledMilestones,
          scheduledHours: data.pipeline.scheduled_hours ?? data.pipeline.scheduledHours,
          backlogMilestones: data.pipeline.backlog_milestones ?? data.pipeline.backlogMilestones,
          backlogHours: data.pipeline.backlog_hours ?? data.pipeline.backlogHours,
        }
      : undefined,
    dropped: data.dropped
      ? {
          failedMaterialAvailabilityMilestones:
            data.dropped.failed_material_availability_milestones ?? data.dropped.failedMaterialAvailabilityMilestones,
          failedMaterialAvailabilityHours:
            data.dropped.failed_material_availability_hours ?? data.dropped.failedMaterialAvailabilityHours,
          outsidePlanningWindowMilestones:
            data.dropped.outside_planning_window_milestones ?? data.dropped.outsidePlanningWindowMilestones,
          notFoundInBpsMilestones: data.dropped.not_found_in_bps_milestones ?? data.dropped.notFoundInBpsMilestones,
        }
      : undefined,
  };
};

// AI Planning Summary Modal Component
interface AiPlanningSummaryModalProps {
  visible: boolean;
  statusMessageData: any;
  onClose: () => void;
}

const AiPlanningSummaryModal: React.FC<AiPlanningSummaryModalProps> = ({ visible, statusMessageData, onClose }) => {
  if (!statusMessageData) return null;

  const { planStatus, capacity, pipeline, dropped } = parseStatusMessageData(statusMessageData) ?? {};
  // Calculate capacity utilization percentage
  const capacityUsedPercent = capacity
    ? Math.round(((capacity.totalCapacityHours - capacity.remainingHours) / capacity.totalCapacityHours) * 100)
    : 0;

  // Helper to format numbers with commas
  const formatNumber = (num: number) => num?.toLocaleString() || '0';

  return (
    <Modal visible={visible} size={ModalSize.LARGE} style={{ width: '520px' }}>
      <AiModalHeaderStyled title={t`AI Planning Summary`}></AiModalHeaderStyled>
      <AiModalBodyStyled>
        <Box px={{ sm: '500' }} pt={{ sm: '400' }} pb={{ sm: '400' }} flexDirection={{ sm: 'column' }}>
          {/* Capacity Section */}
          {capacity && (
            <Box mb={{ sm: '400' }} flexDirection={{ sm: 'column' }}>
              <Box
                mb={{ sm: '300' }}
                pb={{ sm: '100' }}
                style={{ borderBottom: '1px solid #E0E0E0', paddingTop: '6px', paddingBottom: '6px' }}
              >
                <Text size="400" family="graphik" weight="bold" color="grey.1000">
                  <Trans>Capacity Utilization</Trans>
                </Text>
              </Box>
              <Box flexDirection={{ sm: 'column' }}>
                <Box
                  py={{ sm: '150' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px dotted #E0E0E0',
                  }}
                >
                  <Text size="400" family="graphik" color="grey.800">
                    <Trans>Total Capacity</Trans>
                  </Text>
                  <Text size="400" family="graphik" weight="semibold" color="grey.1000">
                    {formatNumber(capacity.totalCapacityHours)} hrs
                  </Text>
                </Box>
                <Box
                  py={{ sm: '150' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px dotted #E0E0E0',
                  }}
                >
                  <Text size="400" family="graphik" color="grey.800">
                    <Trans>Scheduled</Trans>
                  </Text>
                  <Text size="400" family="graphik" weight="semibold" color="grey.1000">
                    {formatNumber(capacity.scheduledHours)} hrs
                  </Text>
                </Box>
                <Box
                  py={{ sm: '150' }}
                  mb={{ sm: '200' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px dotted #E0E0E0',
                  }}
                >
                  <Text size="400" family="graphik" color="grey.800">
                    <Trans>Remaining</Trans>
                  </Text>
                  <Text size="400" family="graphik" weight="semibold" color="green.600">
                    {formatNumber(capacity.remainingHours)} hrs ({100 - capacityUsedPercent}%)
                  </Text>
                </Box>
              </Box>
            </Box>
          )}

          {/* Pipeline Section */}
          {pipeline && (
            <Box mb={{ sm: '400' }} flexDirection={{ sm: 'column' }}>
              <Box
                mb={{ sm: '300' }}
                pb={{ sm: '100' }}
                style={{ borderBottom: '1px solid #E0E0E0', marginBottom: '6px', paddingBottom: '6px' }}
              >
                <Text size="400" family="graphik" weight="bold" color="grey.1000">
                  <Trans>Milestone Pipeline</Trans>
                </Text>
              </Box>
              <Box flexDirection={{ sm: 'column' }}>
                <Box
                  py={{ sm: '150' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px dotted #E0E0E0',
                  }}
                >
                  <Text size="400" family="graphik" color="grey.800">
                    <Trans>Input Milestones</Trans>
                  </Text>
                  <Text size="400" family="graphik" weight="semibold" color="grey.1000">
                    {formatNumber(pipeline.inputMilestones)}
                  </Text>
                </Box>
                <Box
                  py={{ sm: '150' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px dotted #E0E0E0',
                  }}
                >
                  <Text size="400" family="graphik" color="grey.800">
                    <Trans>Eligible for Planning</Trans>
                  </Text>
                  <Text size="400" family="graphik" weight="semibold" color="blue.600">
                    {formatNumber(pipeline.eligibleMilestones)} ({formatNumber(pipeline.eligibleHours)} hrs)
                  </Text>
                </Box>
                <Box
                  py={{ sm: '150' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px dotted #E0E0E0',
                  }}
                >
                  <Text size="400" family="graphik" color="grey.800">
                    <Trans>Scheduled</Trans>
                  </Text>
                  <Text size="400" family="graphik" weight="semibold" color="green.600">
                    {formatNumber(pipeline.scheduledMilestones)} ({formatNumber(pipeline.scheduledHours)} hrs)
                  </Text>
                </Box>
                <Box
                  py={{ sm: '150' }}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '6px',
                    paddingBottom: '6px',
                    borderBottom: '1px dotted #E0E0E0',
                  }}
                >
                  <Text size="400" family="graphik" color="grey.800">
                    <Trans>Backlog</Trans>
                  </Text>
                  <Text size="400" family="graphik" weight="semibold" color="orange.600">
                    {formatNumber(pipeline.backlogMilestones)} ({formatNumber(pipeline.backlogHours)} hrs)
                  </Text>
                </Box>
              </Box>
            </Box>
          )}

          {/* Dropped Milestones Section */}
          {dropped && (
            <Box flexDirection={{ sm: 'column' }}>
              <Box
                mb={{ sm: '300' }}
                pb={{ sm: '100' }}
                style={{ borderBottom: '1px solid #E0E0E0', marginBottom: '6px', paddingBottom: '6px' }}
              >
                <Text size="400" family="graphik" weight="bold" color="grey.1000">
                  <Trans>Dropped Milestones</Trans>
                </Text>
              </Box>
              <Box flexDirection={{ sm: 'column' }}>
                {dropped.failedMaterialAvailabilityMilestones > 0 && (
                  <Box
                    py={{ sm: '150' }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px',
                      paddingBottom: '6px',
                      borderBottom: '1px dotted #E0E0E0',
                    }}
                  >
                    <Text size="400" family="graphik" color="grey.800">
                      <Trans>Failed Material Availability</Trans>
                    </Text>
                    <Text size="400" family="graphik" weight="bold" color="red.600">
                      {formatNumber(dropped.failedMaterialAvailabilityMilestones)} (
                      {formatNumber(dropped.failedMaterialAvailabilityHours)} hrs)
                    </Text>
                  </Box>
                )}
                {dropped.outsidePlanningWindowMilestones > 0 && (
                  <Box
                    py={{ sm: '150' }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px',
                      paddingBottom: '6px',
                      borderBottom: '1px dotted #E0E0E0',
                    }}
                  >
                    <Text size="400" family="graphik" color="grey.800">
                      <Trans>Outside Planning Window</Trans>
                    </Text>
                    <Text size="400" family="graphik" weight="semibold" color="orange.600">
                      {formatNumber(dropped.outsidePlanningWindowMilestones)}
                    </Text>
                  </Box>
                )}
                {dropped.notFoundInBpsMilestones > 0 && (
                  <Box
                    py={{ sm: '150' }}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '6px',
                      paddingBottom: '6px',
                    }}
                  >
                    <Text size="400" family="graphik" color="grey.800">
                      <Trans>Not Found in BPS</Trans>
                    </Text>
                    <Text size="400" family="graphik" weight="semibold" color="grey.600">
                      {formatNumber(dropped.notFoundInBpsMilestones)}
                    </Text>
                  </Box>
                )}
                {dropped.failedMaterialAvailabilityMilestones === 0 &&
                  dropped.outsidePlanningWindowMilestones === 0 &&
                  dropped.notFoundInBpsMilestones === 0 && (
                    <Box
                      py={{ sm: '150' }}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '6px',
                        paddingBottom: '6px',
                      }}
                    >
                      <Text size="400" family="graphik" color="grey.800">
                        <Trans>No milestones were dropped</Trans>
                      </Text>
                    </Box>
                  )}
              </Box>
            </Box>
          )}
        </Box>
      </AiModalBodyStyled>
      <AiModalFooterStyled>
        <Box justifyContent={{ sm: 'flex-end' }}>
          <Button
            appearance={ButtonAppearance.PRIMARY}
            onClick={onClose}
            style={{ minWidth: '84px', background: 'rgb(44, 62, 69)' }}
          >
            <Trans>Got It</Trans>
          </Button>
        </Box>
      </AiModalFooterStyled>
    </Modal>
  );
};

const ConfirmChangeModal: React.FC<ConfirmChangeModalProps> = ({ visible, onCancel, onSubmit }) => {
  const [comment, setComment] = useState('');

  // Reset comment each time the modal opens
  useEffect(() => {
    if (visible) setComment('');
  }, [visible]);

  return (
    <Modal visible={visible} size={ModalSize.LARGE} style={{ width: '496px' }}>
      <AiModalHeaderStyled title={t`Confirm Change`}>
        <ModalClose onClick={onCancel} />
      </AiModalHeaderStyled>
      <AiModalBodyStyled>
        <Box px={{ sm: '400' }} pt={{ sm: '400' }} pb={{ sm: '200' }} flexDirection={{ sm: 'column' }}>
          <Box mb={{ sm: '200' }} style={{ paddingBottom: '5px' }}>
            <Text size="400" family="graphik" color="grey.1000">
              <Trans>Provide a reason for replanning the AI Planned milestone.</Trans>{' '}
              <Text size="400" family="graphik" color="red.600" weight="semibold">
                *
              </Text>
            </Text>
          </Box>
          <Box>
            <TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t`Enter here`}
              rows={4}
              maxLength={120}
              size={TextFieldSize.LARGE}
              required
            />
          </Box>
          <Box mt={{ sm: '200' }} justifyContent={{ sm: 'flex-end' }}>
            <Text size="300" color="grey.600">
              ({comment.length}/120)
            </Text>
          </Box>
        </Box>
      </AiModalBodyStyled>
      <AiModalFooterStyled>
        <Box justifyContent={{ sm: 'flex-end' }} style={{ gap: '8px' }}>
          <Button appearance={ButtonAppearance.SECONDARY} onClick={onCancel} style={{ minWidth: '84px' }}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            appearance={ButtonAppearance.PRIMARY}
            onClick={() => onSubmit(comment)}
            disabled={!comment.trim()}
            style={{ minWidth: '84px', background: 'rgb(44, 62, 69)' }}
          >
            <Trans>Submit</Trans>
          </Button>
        </Box>
      </AiModalFooterStyled>
    </Modal>
  );
};

// Filter milestones based on planningWeeksFilter
const filterMilestones = (milestones: CommodityMilestone[], planningWeeksFilter: PlanningWeeksFilter) => {
  let filteredMilestones: any = milestones;

  // Filter by commodity tag search
  if (!isEmpty(planningWeeksFilter.searchKeyword)) {
    filteredMilestones = filter(filteredMilestones, (milestone: CommodityMilestone) => {
      const tag = milestone.commodity.materialCommodity?.tag || milestone.commodity.weld?.tag;
      return tag && lowerCase(tag).indexOf(lowerCase(planningWeeksFilter.searchKeyword)) !== -1;
    });
  }

  // Filter by milestone type
  if (planningWeeksFilter.milestoneTypeIds && planningWeeksFilter.milestoneTypeIds.length > 0) {
    filteredMilestones = filter(filteredMilestones, (milestone: CommodityMilestone) => {
      return includes(planningWeeksFilter.milestoneTypeIds, milestone?.projectMilestoneTypeId);
    });
  }

  return filteredMilestones;
};

const PlannedMilestones: React.FC<PlannedMilestonesProps> = ({
  setSelectedMilestoneDraggableIds,
  selectedMilestoneDraggableIds,
  draggingMilestoneId,
  milestoneIdentifiers,
  plannedMilestonesData,
  planningWeeksFilter,
  backlogRefetch,
  weekStatsRefetch,
  expiredMilestoneRefetch,
  onAiPlannedMilestoneCount,
  aiStatusMessageData,
  setAiStatusMessageData,
  showAiSummaryModal,
  setShowAiSummaryModal,
}) => {
  const { selectedForeman, planningDispatch } = usePlanning();
  const dispatchToast = useToastsDispatch();

  const { numPlanningNextWeeks } = useConfig();
  const planningWeekDataNoMilestones = getPlanningWeekDataNoMilestones(numPlanningNextWeeks + 1);

  //Get Planned Milestones
  const [getPlannedMilestones, { loading, error, data, refetch, networkStatus }] = useLazyQuery<
    { plannedMilestones: PlanningWeek[] },
    {
      input: PlanningFilterInput;
    }
  >(GET_PLANNED_MILESTONES, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  // Helper to construct query input - shared between foreman change and subscription
  const buildPlannedMilestonesInput = useCallback(() => {
    if (!selectedForeman || selectedForeman.id === UnassignedForemanId || selectedForeman.id === AllForemanId) {
      return null;
    }
    const input: PlanningFilterInput = {
      persona: selectedForeman.persona || PersonaType.FOREMAN,
      crewMemberId: selectedForeman.id,
      numNextWeeks: numPlanningNextWeeks,
    };
    if (selectedForeman?.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED') {
      assign(input, { planningAiRequestId: selectedForeman.planningAiRequest.id });
    }
    return input;
  }, [selectedForeman, numPlanningNextWeeks]);

  // Subscription: reload board when AI planning completes - mimics foreman change behavior
  useSubscription(SUBSCRIPTION_PLANNING_AI_COMPLETED, {
    onData: ({ data }) => {
      const receivedForemanId = data?.data?.planningAiCompleted?.foremanId;
      // Only process if foremanId matches the currently selected foreman.
      // If receivedForemanId is set but no foreman is selected (or it's a different foreman), ignore.
      if (receivedForemanId && (!selectedForeman?.id || receivedForemanId !== selectedForeman.id)) {
        return;
      }
      const receivedStatus = data?.data?.planningAiCompleted?.status;
      const aiPlannedMilestoneCount = data?.data?.planningAiCompleted?.count || 0;
      const statusMessageData = data?.data?.planningAiCompleted?.statusMessageData;
      const statusMessage = data?.data?.planningAiCompleted?.statusMessage;
      const parsedStatusMessageData = parseStatusMessageData(statusMessageData);

      // Store the full statusMessageData for the summary modal
      setAiStatusMessageData(parsedStatusMessageData);

      // Use statusMessage from backend if available, otherwise fall back to parsed planStatus
      let planStatusMessage = statusMessage || '';
      if (!planStatusMessage && parsedStatusMessageData?.planStatus) {
        planStatusMessage = parsedStatusMessageData.planStatus;
      }

      // Update PlanningContext with status from subscription
      if (selectedForeman) {
        planningDispatch({
          type: PlanningActionType.CrewMemberSelected,
          payload: {
            selectedForeman: {
              ...selectedForeman,

              planningAiRequest: {
                ...selectedForeman.planningAiRequest,
                id: data?.data?.planningAiCompleted?.planningAiRequestId,
                status: receivedStatus,
                count: aiPlannedMilestoneCount,
                statusMessageData: statusMessageData,
                statusMessage: statusMessage,
              },
            },
          },
        });
        dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-success' } }); // Remove "AI planning in progress" toast if present

        if (receivedStatus === 'COMPLETED' && onAiPlannedMilestoneCount) {
          onAiPlannedMilestoneCount(aiPlannedMilestoneCount, planStatusMessage);
        }
        // Dispatch count to PlanningContext so sibling components (e.g. WorkPackagesList) can enable Plan with AI
        planningDispatch({
          type: PlanningActionType.SetAiPlannedMilestoneCount,
          payload: { aiPlannedMilestoneCount: aiPlannedMilestoneCount },
        });
        // Show error toast if AI planning failed
        if (receivedStatus === 'ERRORED') {
          dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-error' } });
          dispatchToast({
            type: 'ADD_TOAST',
            payload: {
              id: 'ai-planning-error',
              toastType: ToastType.WARNING,
              content: () => (
                <Text size="500" weight="600">
                  <Trans>AI planning failed. Please try again.</Trans>
                </Text>
              ),
              autoDismissTimeout: 10000, // Auto-close after 10 seconds
            },
          });
        }
      }
    },
  });

  // Subscription: show toast when manual drag-drop save completes
  useSubscription(SUBSCRIPTION_PLANNING_WEEK_SAVED, {
    onData: ({ data }) => {
      const receivedForemanId = data?.data?.planningWeekSaved?.foremanId;
      if (!receivedForemanId || !selectedForeman?.id || receivedForemanId !== selectedForeman.id) {
        return;
      }
      const milestoneCount = data?.data?.planningWeekSaved?.milestoneCount ?? 0;
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'plan-saved-success' } });
      dispatchToast({
        type: 'ADD_TOAST',
        payload: {
          id: 'plan-saved-success',
          toastType: ToastType.INFO,
          content: () => (
            <Text size="500" weight="600">
              <Trans>
                {milestoneCount} milestone{milestoneCount !== 1 ? 's' : ''} planned successfully.
              </Trans>
            </Text>
          ),
          autoDismissTimeout: 5000,
        },
      });
    },
  });

  // Subscription: show toast when milestones are unplanned
  useSubscription(SUBSCRIPTION_MILESTONES_UNPLANNED, {
    onData: ({ data }) => {
      const receivedForemanId = data?.data?.milestonesUnplanned?.foremanId;
      if (!receivedForemanId || !selectedForeman?.id || receivedForemanId !== selectedForeman.id) {
        return;
      }
      const milestoneCount = data?.data?.milestonesUnplanned?.milestoneCount ?? 0;
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'milestones-unplanned-success' } });
      dispatchToast({
        type: 'ADD_TOAST',
        payload: {
          id: 'milestones-unplanned-success',
          toastType: ToastType.INFO,
          content: () => (
            <Text size="500" weight="600">
              <Trans>
                {milestoneCount} milestone{milestoneCount !== 1 ? 's' : ''} unplanned successfully.
              </Trans>
            </Text>
          ),
          autoDismissTimeout: 5000,
        },
      });
    },
  });

  // Monitor AI planning status and show spinner while processing
  useEffect(() => {
    const input = buildPlannedMilestonesInput();
    if (input) {
      getPlannedMilestones({ variables: { input } });
    }
  }, [getPlannedMilestones, buildPlannedMilestonesInput]);
  useEffect(() => {
    const milestones = get(data, 'plannedMilestones', []);
    if (plannedMilestonesData) {
      plannedMilestonesData.current = milestones;
    }

    // Calculate AI milestone count if callback provided
    if (onAiPlannedMilestoneCount && selectedForeman?.planningAiRequest?.id) {
      const currentAiRequestId = selectedForeman.planningAiRequest.id;
      let count = 0;
      milestones.forEach((week: PlanningWeek) => {
        if (week.milestones && week.milestones.length > 0) {
          count += week.milestones.filter((milestone) => milestone.planningAiRequestId === currentAiRequestId).length;
        }
      });
      onAiPlannedMilestoneCount(count);
    } else if (onAiPlannedMilestoneCount) {
      onAiPlannedMilestoneCount(0);
    }
  }, [plannedMilestonesData, data, onAiPlannedMilestoneCount, selectedForeman?.planningAiRequest?.id]);

  const plannedMilestones: PlanningWeek[] = useMemo(() => {
    return get(data, 'plannedMilestones', planningWeekDataNoMilestones);
  }, [data]);

  const plannedMilestonesSorted =
    selectedForeman?.id === UnassignedForemanId || selectedForeman?.id == AllForemanId
      ? planningWeekDataNoMilestones
      : sortBy(plannedMilestones, ['year', 'weekNumber']);
  // Get planningAiRequestId from selectedForeman if present
  const planningAiRequestId = selectedForeman?.planningAiRequest?.id;

  return (
    <Fragment>
      {!loading && error && (
        <div data-testid="retry">
          <ErrorRetry
            refetch={() =>
              refetch({
                input: {
                  persona: selectedForeman && selectedForeman.persona ? selectedForeman.persona : PersonaType.FOREMAN,
                  crewMemberId: selectedForeman ? selectedForeman.id : '',
                  numNextWeeks: numPlanningNextWeeks,
                },
              })
            }
          >
            <Trans>There was a problem loading planned milestones. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </div>
      )}
      {!loading &&
        !error &&
        plannedMilestones.length > 0 &&
        map(plannedMilestonesSorted, (plannedItem) => {
          // apply planning weeks filter to planned milestones
          const filteredMilestones = plannedItem.milestones?.length
            ? filterMilestones(plannedItem.milestones, planningWeeksFilter)
            : [];

          return (
            <Droppable key={plannedItem.id} droppableId={plannedItem.id}>
              {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => {
                return (
                  <WeekColumnDroppableStyled
                    style={numPlanningNextWeeks == 3 ? { width: '25%' } : { width: '33.33%' }}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    {...snapshot}
                  >
                    <VisuallyHidden>{provided.placeholder}</VisuallyHidden>
                    {filteredMilestones && filteredMilestones.length > 0 && (
                      <CommodityCards
                        commodityMilestones={filteredMilestones}
                        droppableId={plannedItem.id}
                        setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
                        selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
                        draggingMilestoneId={draggingMilestoneId}
                        milestoneIdentifiers={milestoneIdentifiers}
                        planningAiRequestId={planningAiRequestId}
                      />
                    )}
                  </WeekColumnDroppableStyled>
                );
              }}
            </Droppable>
          );
        })}
    </Fragment>
  );
};

const PlanningViewDnd: React.FC<
  PlanProps & {
    onAiPlannedMilestoneCount?: (count: number, message?: string) => void;
    aiStatusMessageData: any;
    setAiStatusMessageData: (data: any) => void;
    showAiSummaryModal: boolean;
    setShowAiSummaryModal: (show: boolean) => void;
  }
> = ({
  expiredMilestoneStats,
  onAiPlannedMilestoneCount,
  aiStatusMessageData,
  setAiStatusMessageData,
  showAiSummaryModal,
  setShowAiSummaryModal,
}) => {
  const plannedMilestoneData = React.useRef<PlanningWeek[]>([]);

  const { numPlanningNextWeeks } = useConfig();
  const { selectedWpList, selectedForeman, selectedGeneralForeman, selectedSuperintendent } = usePlanning();
  const { canPlanMilestones } = usePlanningPermissions();
  // NOTE: these state entities are only used for multi drag
  const [selectedMilestoneDraggableIds, setSelectedMilestoneDraggableIds] = useState<string[]>([]);
  const [draggingMilestoneId, setDraggingMilestoneId] = useState<string>('');
  const [activeSelectionSection, setActiveSelectionSection] = useState<SelectionSection | null>(null);
  const [isBacklogCollapsed, setIsBacklogCollapsed] = useState(false);

  // State for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingDragProps, setPendingDragProps] = useState<any>(null);
  const [pendingSelectedIds, setPendingSelectedIds] = useState<string[]>([]);

  // Lazy query for expired milestones count
  const [getExpiredMilestoneCount] = useLazyQuery<
    { expiredMilestoneCount: CrewMemberExpiredMilestoneStats },
    { input: PlanningCrewMemberInput }
  >(EXPIRED_MILESTONES_COUNT, { fetchPolicy: 'network-only', nextFetchPolicy: 'cache-first' });

  // Helper to determine which persona to query for expired milestones
  const getExpiredMilestonePersona = useCallback(() => {
    const isAllOrUnassignedSup =
      selectedSuperintendent?.id === AllSuperintendentId || selectedSuperintendent?.id === UnassignedSuperintendentId;
    if (isAllOrUnassignedSup) return null;

    if (
      selectedGeneralForeman?.id === UnassignedGeneralForemanId ||
      selectedGeneralForeman?.id === AllGeneralForemanId
    ) {
      return selectedSuperintendent;
    } else if (selectedForeman?.id === UnassignedForemanId || selectedForeman?.id === AllForemanId) {
      return selectedGeneralForeman;
    }
    return selectedForeman;
  }, [selectedForeman, selectedGeneralForeman, selectedSuperintendent]);

  // Refetch expired milestone count
  const refetchExpiredMilestones = useCallback(() => {
    const filterPersona = getExpiredMilestonePersona();
    if (!filterPersona) return;

    getExpiredMilestoneCount({
      variables: {
        input: {
          persona: filterPersona.persona,
          crewMemberId: filterPersona.id,
        },
      },
    }).then((result) => {
      if (result.data && expiredMilestoneStats) {
        expiredMilestoneStats.current = result.data.expiredMilestoneCount || {
          id: '0',
          numExpiredMilestones: 0,
        };
      }
    });
  }, [getExpiredMilestoneCount, getExpiredMilestonePersona, expiredMilestoneStats]);
  const [planningWeeksFilter, setPlanningWeeksFilter] = useState<PlanningWeeksFilter>({
    searchKeyword: '',
    milestoneTypeIds: [],
  });
  const backlogMilestoneData = React.useRef<CommodityMilestone[]>([]);
  const planningWeekStats = React.useRef<PlanningWeekStats[]>([]);
  const backlogRefetch = React.useRef<(() => void) | null>(null);
  const weekStatsRefetch = React.useRef<(() => void) | null>(null);
  const expiredMilestoneRefetch = React.useRef<(() => void) | null>(null);

  // Expose expired milestone refetch function
  useEffect(() => {
    expiredMilestoneRefetch.current = refetchExpiredMilestones;
  }, [refetchExpiredMilestones]);

  const milestoneIdentifiers = React.useRef<Map<string, MilestoneIdentifier>>(new Map());
  // const [claimedApprovedMilestones, setClaimedApprovedMilestone] = useState<CommodityMilestone[]>([]);
  const [planningError, setPlanningError] = useState<PlanningError[]>([]);
  const [savePlan, { error: savePlanError }] = useMutation<
    { savePlanning: PlanningPayload },
    { input: SavePlanningInput }
  >(SAVE_PLAN);
  const [unplan] = useMutation<{ unplan: PlanningPayload }, { input: UnplanInput }>(UNPLAN);
  const onDragStartCallback = useCallback(
    (start: DragStart) => {
      const id: string = start.draggableId;
      const selectedMilestoneDraggableId = find(selectedMilestoneDraggableIds, (milestoneId) => milestoneId === id);
      // if dragging an item that is not selected - unselect all items
      if (!selectedMilestoneDraggableId) {
        setSelectedMilestoneDraggableIds([]);
      }
      setDraggingMilestoneId(id);
    },
    [selectedMilestoneDraggableIds]
  );

  const onDragEndMulti = async ({ destination }, selectedMilestoneDraggableIds, comment = '') => {
    const commodityMilestoneIds: string[] = [];
    const commodityMilestonesForOptimisticUpdate: CommodityMilestone[] = [];
    const milestoneSource: Map<string | undefined, string | undefined> = new Map();

    // NOTE: we bail out early if the source was not dropped into a drop target
    forEach(selectedMilestoneDraggableIds, (draggableId) => {
      if (!destination) return;
      // NOTE: a drag id is a composition of up to five things:
      // {droppable.id}___COMMODITY___{commodity.id}___MILESTONE___{milestone.id}
      // if just a commodity is dragged then it will just be the first three entities
      const identifiers = splitIdentifiers(draggableId);
      // NOTE: this will let us know where the item originated from based on droppable.id
      const sourceStateKey = nth(identifiers, 0);
      // NOTE: this identifier lets us know what column we will be dropping into
      // ideally this is something unique to the given week (such as the date)
      const destinationStateKey = destination.droppableId;
      // NOTE: we bail out early if the drop target is the same as the source
      if (sourceStateKey === destinationStateKey) return;
      // NOTE: milestone.id may NOT exist since a user can choose between dragging a milestone or
      // the entire commodity all at once
      const milestoneId = nth(identifiers, 4);
      milestoneSource.set(milestoneId, sourceStateKey);
      // NOTE: If the commodityId is exists and the milestoneId doesn't exists that means user dragged a commodity.
      // get all the milestones id from the commodity;
      if (sourceStateKey !== DND_IDENTIFIER.Unplanned) {
        const sourcePlaningWeek = find(plannedMilestoneData.current, { id: sourceStateKey });
        const foundCommodityMilestone = find(
          sourcePlaningWeek?.milestones,
          (milestone) => milestone.id === milestoneId
        );
        if (foundCommodityMilestone) {
          commodityMilestoneIds.push(foundCommodityMilestone?.id);
          commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
        }
      } else {
        const foundCommodityMilestone = find(backlogMilestoneData.current, (milestone) => milestone.id === milestoneId);
        if (foundCommodityMilestone) {
          commodityMilestoneIds.push(foundCommodityMilestone?.id);
          commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
        }
      }
    });
    if (commodityMilestoneIds.length > 0) {
      const destinationStateKey = destination.droppableId;
      const destPlanningWeekIdentifiers = splitIdentifiers(destinationStateKey, '|');
      //@ts-ignore destPlaningWeek be PlanningWeek|undefined
      const destPlaningWeek: PlanningWeek | undefined = find(plannedMilestoneData.current, {
        id: destinationStateKey,
      });
      if (!destPlaningWeek) return;
      const savePlanningInput: SavePlanningInput = {
        planningWeekId: String(nth(destPlanningWeekIdentifiers, 2)),
        year: parseInt(String(nth(destPlanningWeekIdentifiers, 0))),
        week: parseInt(String(nth(destPlanningWeekIdentifiers, 1))),
        commodityMilestoneIds: commodityMilestoneIds,
        foremanId: selectedForeman ? selectedForeman.id : '',
        generalForemanId: selectedGeneralForeman ? selectedGeneralForeman.id : '',
        superintendentId: selectedSuperintendent ? selectedSuperintendent.id : '',
        comment: comment || undefined,
      };
      //@ts-ignore destPlaningWeek will be PlanningWeek object
      const optimisticResponse = getSavePlanningOptimisticResponse(
        destPlaningWeek,
        commodityMilestonesForOptimisticUpdate,
        planningWeekStats.current,
        milestoneSource,
        expiredMilestoneStats?.current
      );
      await savePlan({
        variables: {
          input: savePlanningInput,
        },
        optimisticResponse: optimisticResponse,
        update: (cache, { data }) => {
          if (data && data.savePlanning.commodityMilestones && data.savePlanning.commodityMilestones.length > 0) {
            // Group milestones by their source state key to batch cache updates
            const milestonesBySource = new Map<string, CommodityMilestone[]>();

            forEach(data.savePlanning.commodityMilestones, (commodityMilestone) => {
              const sourceStateKey = milestoneSource.get(commodityMilestone.id);
              if (!sourceStateKey) return;

              if (!milestonesBySource.has(sourceStateKey)) {
                milestonesBySource.set(sourceStateKey, []);
              }
              milestonesBySource.get(sourceStateKey)!.push(commodityMilestone);
            });

            // Call updateCache once per source group (not N times per milestone)
            // Only pass planningAiRequestId when status is 'COMPLETED' to match query variables
            const cacheRequestId =
              selectedForeman?.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED'
                ? selectedForeman.planningAiRequest.id
                : null;
            milestonesBySource.forEach((milestones, sourceStateKey) => {
              updateCache(
                cache,
                sourceStateKey,
                destinationStateKey,
                milestones,
                selectedForeman ? selectedForeman?.id : '',
                selectedWpList ? selectedWpList : [],
                numPlanningNextWeeks,
                cacheRequestId
              );
            });
          }

          if (data && data.savePlanning.error && data.savePlanning.error.length > 0) {
            setPlanningError(data.savePlanning.error);
            deleteApproveClaimedMilestone(
              data.savePlanning.error,
              milestoneSource,
              cache,
              destinationStateKey,
              selectedForeman ? selectedForeman?.id : '',
              selectedWpList ? selectedWpList : [],
              numPlanningNextWeeks
            );
          }
        },
      });
    }
  };

  const onDragEndSingle = async ({ destination, draggableId, source }, comment = '') => {
    // NOTE: we bail out early if the source was not dropped into a drop target
    if (!destination) return;

    const [sourceStateKey] = splitIdentifiers(source.droppableId); //sourceStateKey: "2020|21|3071629f-356e-428a-8ce2-f55938bb8055"
    // NOTE: this identifier lets us know what column we will be dropping into
    // ideally this is something unique to the given week (such as the date)
    const destinationStateKey = destination.droppableId; //"destinationStateKey: 2020|21|3071629f-356e-428a-8ce2-f55938bb8055"
    // NOTE: we bail out early if the drop target is the same as the source
    if (sourceStateKey === destinationStateKey) return;
    // NOTE: a drag id is a composition of up to five things:
    // {droppable.id}___COMMODITY___{commodity.id}___MILESTONE___{milestone.id}
    // if just a commodity is dragged then it will just be the first three entities
    const identifiers = splitIdentifiers(draggableId);
    // NOTE: we know commodity.id will always exist on a draggable entity
    const commodityId = nth(identifiers, 2);
    // NOTE: milestone.id may NOT exist since a user can choose between dragging a milestone or
    // the entire commodity all at once
    const milestoneId = nth(identifiers, 4);
    let commodityMilestoneIds = milestoneId ? [milestoneId] : [];
    const commodityMilestonesForOptimisticUpdate: CommodityMilestone[] = [];
    // NOTE: If the commodityId is exists and the milestoneId doesn't exists that means user dragged a commodity.
    // get all the milestones id from the commodity;
    if (sourceStateKey !== DND_IDENTIFIER.Unplanned) {
      const sourcePlaningWeek = find(plannedMilestoneData.current, { id: sourceStateKey });
      const foundCommodityMilestones = filter(sourcePlaningWeek?.milestones, (milestone) => {
        if (!milestoneId && commodityId) {
          return (
            milestone.commodity.materialCommodity?.id === commodityId || milestone.commodity.weld?.id === commodityId
          );
        } else {
          return milestone.id === milestoneId;
        }
      });
      const foundCommodityMilestoneIds = map(foundCommodityMilestones, ({ id }) => id);
      commodityMilestoneIds = foundCommodityMilestoneIds;
      commodityMilestonesForOptimisticUpdate.push(...foundCommodityMilestones);
    } else {
      const foundCommodityMilestones = filter(backlogMilestoneData.current, (milestone) => {
        if (!milestoneId && commodityId) {
          return (
            milestone.commodity.materialCommodity?.id === commodityId || milestone.commodity.weld?.id === commodityId
          );
        } else {
          return milestone.id === milestoneId;
        }
      });
      const foundCommodityMilestoneIds = map(foundCommodityMilestones, ({ id }) => id);
      commodityMilestoneIds = foundCommodityMilestoneIds;
      commodityMilestonesForOptimisticUpdate.push(...foundCommodityMilestones);
    }

    const destPlanningWeekIdentifiers = splitIdentifiers(destinationStateKey, '|');
    //@ts-ignore destPlaningWeek be PlanningWeek|undefined
    const destPlaningWeek: PlanningWeek | undefined = find(plannedMilestoneData.current, { id: destinationStateKey });
    if (!destPlaningWeek) return;
    const savePlanningInput: SavePlanningInput = {
      planningWeekId: String(nth(destPlanningWeekIdentifiers, 2)),
      year: parseInt(String(nth(destPlanningWeekIdentifiers, 0))),
      week: parseInt(String(nth(destPlanningWeekIdentifiers, 1))),
      commodityMilestoneIds: commodityMilestoneIds,
      foremanId: selectedForeman ? selectedForeman.id : '',
      generalForemanId: selectedGeneralForeman ? selectedGeneralForeman.id : '',
      superintendentId: selectedSuperintendent ? selectedSuperintendent.id : '',
      comment: comment || undefined,
    };
    const milestoneSource: Map<string | undefined, string | undefined> = new Map();
    forEach(commodityMilestoneIds, (commodityMilestoneId) => milestoneSource.set(commodityMilestoneId, sourceStateKey));
    //@ts-ignore destPlaningWeek will be PlanningWeek object
    const optimisticResponse = getSavePlanningOptimisticResponse(
      destPlaningWeek,
      commodityMilestonesForOptimisticUpdate,
      planningWeekStats.current,
      milestoneSource,
      expiredMilestoneStats?.current
    );
    await savePlan({
      variables: {
        input: savePlanningInput,
      },
      optimisticResponse: optimisticResponse,
      update: (cache, { data }) => {
        if (
          data &&
          data.savePlanning.commodityMilestones &&
          data.savePlanning.commodityMilestones.length > 0 &&
          selectedForeman?.id
        ) {
          // Group milestones by their source state key to batch cache updates
          const milestonesBySource = new Map<string, CommodityMilestone[]>();

          forEach(data.savePlanning.commodityMilestones, (commodityMilestone) => {
            const sourceStateKey = milestoneSource.get(commodityMilestone.id);
            if (!sourceStateKey) return;

            if (!milestonesBySource.has(sourceStateKey)) {
              milestonesBySource.set(sourceStateKey, []);
            }
            milestonesBySource.get(sourceStateKey)!.push(commodityMilestone);
          });

          // Call updateCache once per source group (not N times per milestone)
          // Only pass planningAiRequestId when status is 'COMPLETED' to match query variables
          const cacheRequestId =
            selectedForeman?.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED'
              ? selectedForeman.planningAiRequest.id
              : null;
          milestonesBySource.forEach((milestones, sourceStateKey) => {
            updateCache(
              cache,
              sourceStateKey,
              destinationStateKey,
              milestones,
              selectedForeman ? selectedForeman?.id : '',
              selectedWpList ? selectedWpList : [],
              numPlanningNextWeeks,
              cacheRequestId
            );
          });
        }
        if (data && data.savePlanning.error && data.savePlanning.error.length > 0) {
          setPlanningError(data.savePlanning.error);
          deleteApproveClaimedMilestone(
            data.savePlanning.error,
            milestoneSource,
            cache,
            destinationStateKey,
            selectedForeman ? selectedForeman?.id : '',
            selectedWpList ? selectedWpList : [],
            numPlanningNextWeeks
          );
        }
      },
    });
  };

  // Handler to show confirmation modal and store drag props
  const handleConfirmationModal = useCallback((dragProps, selectedIds: string[]) => {
    setPendingDragProps(dragProps);
    setPendingSelectedIds(selectedIds);
    setShowConfirmModal(true);
  }, []);

  // Handler when user submits the confirmation modal
  const handleConfirmSubmit = useCallback(
    (comment: string) => {
      if (!pendingDragProps) return;

      // Call the appropriate handler with the comment
      if (pendingSelectedIds.length > 0) {
        onDragEndMulti(pendingDragProps, pendingSelectedIds, comment);
      } else {
        onDragEndSingle(pendingDragProps, comment);
      }

      // Reset state
      setShowConfirmModal(false);
      setPendingDragProps(null);
      setPendingSelectedIds([]);
      setSelectedMilestoneDraggableIds([]);
      setDraggingMilestoneId('');
    },
    [pendingDragProps, pendingSelectedIds]
  );

  // Handler when user cancels the confirmation modal
  const handleConfirmCancel = useCallback(() => {
    setShowConfirmModal(false);
    setPendingDragProps(null);
    setPendingSelectedIds([]);
    setDraggingMilestoneId('');
  }, []);

  const onDragEndCallback = useCallback(
    (dragProps) => {
      // Check if destination exists (if not, user dropped outside valid area)
      if (!dragProps.destination) {
        setSelectedMilestoneDraggableIds([]);
        setDraggingMilestoneId('');
        return;
      }

      // Determine source droppableId - extract just the week part (before ___)
      const sourceDroppableIdRaw =
        dragProps.source?.droppableId || (dragProps.draggableId && dragProps.draggableId.split('___')[0]);
      const sourceDroppableId = sourceDroppableIdRaw ? sourceDroppableIdRaw.split('___')[0] : '';

      // Extract just the week part from destination too
      const destinationDroppableIdRaw = dragProps.destination?.droppableId;
      const destinationDroppableId = destinationDroppableIdRaw ? destinationDroppableIdRaw.split('___')[0] : '';

      // If source is backlog, do not show modal
      if (sourceDroppableId === DND_IDENTIFIER.Unplanned) {
        // Directly call drag end handler without modal
        if (selectedMilestoneDraggableIds.length > 0) {
          onDragEndMulti(dragProps, selectedMilestoneDraggableIds);
        } else {
          onDragEndSingle(dragProps);
        }
        return;
      }

      // If dropped in same week, do not show modal
      if (sourceDroppableId === destinationDroppableId) {
        // Directly call drag end handler without modal
        if (selectedMilestoneDraggableIds.length > 0) {
          onDragEndMulti(dragProps, selectedMilestoneDraggableIds);
        } else {
          onDragEndSingle(dragProps);
        }
        return;
      }

      // Build a flat Set of AI-planned milestone IDs for O(1) lookup
      const aiPlannedIds = new Set<string>();
      for (const week of plannedMilestoneData.current) {
        for (const m of week.milestones ?? []) {
          if (m.planningAiRequestId) aiPlannedIds.add(m.id);
        }
      }

      // Check if any dragged milestone is AI-planned
      let hasAiPlanned: boolean;
      if (selectedMilestoneDraggableIds.length > 0) {
        hasAiPlanned = selectedMilestoneDraggableIds.some((draggableId) => {
          const milestoneId = nth(splitIdentifiers(draggableId), 4);
          return Boolean(milestoneId && aiPlannedIds.has(String(milestoneId)));
        });
      } else {
        const milestoneId = nth(splitIdentifiers(dragProps.draggableId), 4);
        hasAiPlanned = Boolean(milestoneId && aiPlannedIds.has(String(milestoneId)));
      }

      if (hasAiPlanned) {
        // Show confirmation modal
        if (selectedMilestoneDraggableIds.length > 0) {
          handleConfirmationModal(dragProps, selectedMilestoneDraggableIds);
        } else {
          handleConfirmationModal(dragProps, []);
        }
      } else {
        // No AI planned milestones, proceed without modal
        if (selectedMilestoneDraggableIds.length > 0) {
          onDragEndMulti(dragProps, selectedMilestoneDraggableIds);
        } else {
          onDragEndSingle(dragProps);
        }
      }
    },
    [selectedMilestoneDraggableIds, handleConfirmationModal, plannedMilestoneData]
  );

  const handleUnplanMilestones = useCallback(async () => {
    const commodityMilestoneIds: string[] = [];
    const commodityMilestonesForOptimisticUpdate: CommodityMilestone[] = [];
    const milestoneSource: Map<string | undefined, string | undefined> = new Map();
    //destination is backlog
    const destinationStateKey = DND_IDENTIFIER.Unplanned;
    forEach(selectedMilestoneDraggableIds, (draggableId) => {
      // NOTE: a drag id is a composition of up to five things:
      // {droppable.id}___COMMODITY___{commodity.id}___MILESTONE___{milestone.id}
      const identifiers = splitIdentifiers(draggableId);
      // NOTE: this will let us know where the item originated from based on droppable.id
      const sourceStateKey = nth(identifiers, 0);
      const isMilestoneExpired = nth(identifiers, 5) === DND_IDENTIFIER.Expired;
      //return if user tries to unplan already unplanned milestones unless they are expired
      if (sourceStateKey === destinationStateKey && !isMilestoneExpired) return;

      const milestoneId = nth(identifiers, 4);
      milestoneSource.set(milestoneId, sourceStateKey);
      if (sourceStateKey === DND_IDENTIFIER.Unplanned && isMilestoneExpired) {
        const foundCommodityMilestone = find(backlogMilestoneData.current, (milestone) => milestone.id === milestoneId);
        if (foundCommodityMilestone) {
          commodityMilestoneIds.push(foundCommodityMilestone?.id);
          commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
        }
      } else if (destinationStateKey === DND_IDENTIFIER.Unplanned) {
        //unplan planned milestones back to backlog
        forEach(plannedMilestoneData.current, (planninkWeek) => {
          if (!planninkWeek || !planninkWeek.milestones || !planninkWeek.milestones.length) return;
          const foundCommodityMilestone = find(planninkWeek.milestones, (milestone) => milestone.id === milestoneId);
          if (foundCommodityMilestone) {
            commodityMilestoneIds.push(foundCommodityMilestone?.id);
            commodityMilestonesForOptimisticUpdate.push(foundCommodityMilestone);
          }
        });
      }
    });
    if (commodityMilestoneIds && commodityMilestoneIds.length) {
      const optimisticResponse = getUnplanOptimisticResponse(
        commodityMilestonesForOptimisticUpdate,
        planningWeekStats.current,
        milestoneSource,
        expiredMilestoneStats?.current
      );

      const unplanInput: UnplanInput = {
        commodityMilestoneIds: commodityMilestoneIds,
      };
      const plannedMilestonesSnapshot = plannedMilestoneData.current;
      const backlogMilestonesSnapshot = selectedWpList?.length ? backlogMilestoneData.current : null;
      await unplan({
        variables: {
          input: unplanInput,
        },
        optimisticResponse: optimisticResponse,
        update: (cache, { data }) => {
          if (data && data.unplan && data.unplan.commodityMilestones && data.unplan.commodityMilestones.length) {
            // Only pass planningAiRequestId when status is 'COMPLETED' to match query variables
            const cacheRequestId =
              selectedForeman?.planningAiRequest?.id && selectedForeman.planningAiRequest.status === 'COMPLETED'
                ? selectedForeman.planningAiRequest.id
                : null;
            updateUnplanCache(
              cache,
              destinationStateKey,
              data.unplan.commodityMilestones,
              selectedWpList ? selectedWpList : [],
              selectedForeman ? selectedForeman?.id : '',
              numPlanningNextWeeks,
              plannedMilestonesSnapshot,
              backlogMilestonesSnapshot,
              cacheRequestId
            );
          }
        },
      });
    }
  }, [
    selectedMilestoneDraggableIds,
    selectedWpList,
    selectedForeman,
    numPlanningNextWeeks,
    expiredMilestoneStats,
    unplan,
  ]);

  const unplanMilestonesCallback = useCallback(() => {
    if (selectedMilestoneDraggableIds && selectedMilestoneDraggableIds.length) {
      handleUnplanMilestones();
      setSelectedMilestoneDraggableIds([]);
    }
  }, [selectedMilestoneDraggableIds, handleUnplanMilestones]);

  // Handle select all milestones for a specific week
  const handleSelectWeekMilestones = useCallback(
    (weekNumber: number) => {
      if (!plannedMilestoneData.current || !plannedMilestoneData.current.length) {
        return;
      }
      const selectedPlannedWeek = find(
        plannedMilestoneData.current,
        (plannedWeek) => plannedWeek.weekNumber === weekNumber
      );

      if (!selectedPlannedWeek || !selectedPlannedWeek.milestones) {
        return;
      }

      // Apply filters to get only visible milestones
      const filteredMilestones = filterMilestones(selectedPlannedWeek.milestones, planningWeeksFilter);

      const weekMilestoneIds: string[] = [];
      filteredMilestones.forEach((milestone) => {
        const milestoneIdentifier = milestoneIdentifiers.current.get(milestone.id);
        if (milestoneIdentifier && milestoneIdentifier.canPlan) {
          weekMilestoneIds.push(milestoneIdentifier.dragIdentifier);
        }
      });

      setSelectedMilestoneDraggableIds(weekMilestoneIds);
    },
    [plannedMilestoneData, milestoneIdentifiers, selectedMilestoneDraggableIds, planningWeeksFilter]
  );

  // Handle planning weeks filter change
  const handlePlanningWeeksFilterChange = useCallback((filter: PlanningWeeksFilter) => {
    setPlanningWeeksFilter(filter);
    // Clear all selections when filter changes
    setSelectedMilestoneDraggableIds([]);
    setActiveSelectionSection(null);
  }, []);

  const handleToggleBacklogCollapse = useCallback(() => {
    setIsBacklogCollapsed(!isBacklogCollapsed);
  }, [isBacklogCollapsed]);

  // Handle select all across all weeks
  const handleSelectAllWeeks = useCallback(
    (selected: boolean) => {
      if (!plannedMilestoneData.current || plannedMilestoneData.current.length === 0) return;

      if (selected) {
        // Set active section to planned (clears backlog checkbox)
        setActiveSelectionSection(SelectionSection.PLANNED);

        const allWeekMilestoneIds: string[] = [];
        // Collect all plannable milestone IDs from all weeks
        plannedMilestoneData.current.forEach((week) => {
          if (!week.milestones) return;

          // Apply filters to get only visible milestones
          const filteredMilestones = filterMilestones(week.milestones, planningWeeksFilter);

          filteredMilestones.forEach((milestone) => {
            const milestoneIdentifier = milestoneIdentifiers.current.get(milestone.id);
            if (milestoneIdentifier && milestoneIdentifier.canPlan) {
              allWeekMilestoneIds.push(milestoneIdentifier.dragIdentifier);
            }
          });
        });

        // Replace all selections with planned milestones
        setSelectedMilestoneDraggableIds(allWeekMilestoneIds);
      } else {
        // Clear active section and all selections
        setActiveSelectionSection(null);
        setSelectedMilestoneDraggableIds([]);

        // Refetch backlog milestones with current filter
        if (backlogRefetch.current) {
          backlogRefetch.current();
        }
      }
    },
    [plannedMilestoneData, planningWeeksFilter, milestoneIdentifiers, backlogMilestoneData]
  );

  // Calculate select all state for all weeks
  const getPlannedMilestonesSelectState = useMemo(() => {
    if (!plannedMilestoneData.current || plannedMilestoneData.current.length === 0) {
      return { isAllSelected: false, isIndeterminate: false };
    }

    // If backlog section is active, planned should be unchecked
    if (activeSelectionSection === SelectionSection.BACKLOG) {
      return { isAllSelected: false, isIndeterminate: false };
    }

    let plannableCount = 0;
    let selectedCount = 0;
    plannedMilestoneData.current.forEach((week) => {
      if (!week.milestones) return;

      // Apply filters to get only visible milestones
      const filteredMilestones = filterMilestones(week.milestones, planningWeeksFilter);

      filteredMilestones.forEach((milestone) => {
        const milestoneIdentifier = milestoneIdentifiers.current.get(milestone.id);
        if (milestoneIdentifier && milestoneIdentifier.canPlan) {
          plannableCount++;
          if (selectedMilestoneDraggableIds.includes(milestoneIdentifier.dragIdentifier)) {
            selectedCount++;
          }
        }
      });
    });

    return {
      isAllSelected: plannableCount > 0 && selectedCount === plannableCount,
      isIndeterminate: selectedCount > 0 && selectedCount < plannableCount,
    };
  }, [
    plannedMilestoneData,
    planningWeeksFilter,
    milestoneIdentifiers,
    selectedMilestoneDraggableIds,
    activeSelectionSection,
  ]);

  return (
    <PlanningContentContainer>
      {savePlanError && <SavePlanError savePlanError={savePlanError} />}
      {planningError && planningError.length > 0 && (
        <PlanMilestoneError planningError={planningError} setPlanningError={setPlanningError} />
      )}
      <DragDropContext onDragEnd={onDragEndCallback} onDragStart={onDragStartCallback}>
        <Backlog
          setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
          selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
          draggingMilestoneId={draggingMilestoneId}
          unplanMilestones={unplanMilestonesCallback}
          milestoneIdentifiers={milestoneIdentifiers}
          backlogMilestoneData={backlogMilestoneData}
          backlogRefetch={backlogRefetch}
          activeSelectionSection={activeSelectionSection}
          setActiveSelectionSection={setActiveSelectionSection}
          isCollapsed={isBacklogCollapsed}
          onToggleCollapse={handleToggleBacklogCollapse}
        />
        <ColumnContainerStyled
          style={
            isBacklogCollapsed
              ? { width: 'calc(100% - 40px)' }
              : numPlanningNextWeeks == 3
                ? { width: '79%' }
                : { width: '74%' }
          }
        >
          <PlanningWeeksHeader
            filter={planningWeeksFilter}
            onFilterChange={handlePlanningWeeksFilterChange}
            onSelectAll={handleSelectAllWeeks}
            isAllSelected={getPlannedMilestonesSelectState.isAllSelected}
            isIndeterminate={getPlannedMilestonesSelectState.isIndeterminate}
            disabled={selectedForeman?.id === UnassignedForemanId || selectedForeman?.id === AllForemanId}
            selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
            unplanMilestones={unplanMilestonesCallback}
            canPlanMilestones={canPlanMilestones}
          />
          <PlanningWeekCard
            planWeekStats={planningWeekStats}
            onSelectWeekMilestones={handleSelectWeekMilestones}
            weekStatsRefetch={weekStatsRefetch}
          />
          <WeekPlanDetailScrollable>
            <WeekPlanDetailContainer>
              <PlannedMilestones
                setSelectedMilestoneDraggableIds={setSelectedMilestoneDraggableIds}
                selectedMilestoneDraggableIds={selectedMilestoneDraggableIds}
                draggingMilestoneId={draggingMilestoneId}
                milestoneIdentifiers={milestoneIdentifiers}
                plannedMilestonesData={plannedMilestoneData}
                planningWeeksFilter={planningWeeksFilter}
                backlogRefetch={backlogRefetch}
                weekStatsRefetch={weekStatsRefetch}
                expiredMilestoneRefetch={expiredMilestoneRefetch}
                onAiPlannedMilestoneCount={onAiPlannedMilestoneCount}
                aiStatusMessageData={aiStatusMessageData}
                setAiStatusMessageData={setAiStatusMessageData}
                showAiSummaryModal={showAiSummaryModal}
                setShowAiSummaryModal={setShowAiSummaryModal}
              />
            </WeekPlanDetailContainer>
          </WeekPlanDetailScrollable>
        </ColumnContainerStyled>
      </DragDropContext>
      <ConfirmChangeModal visible={showConfirmModal} onCancel={handleConfirmCancel} onSubmit={handleConfirmSubmit} />
    </PlanningContentContainer>
  );
};

const foremanToast = (numPlanningNextWeeks: number) => {
  return (
    <Box p={{ sm: '550' }} flexDirection={{ sm: 'column' }}>
      <Box mb={{ sm: '450' }}>
        <Text family="graphik" weight="600" size="500">
          <Trans>You must select a Foreman before you can</Trans>
        </Text>
      </Box>
      <Text family="graphik" weight="600" size="500">
        {t`make changes to the ${numPlanningNextWeeks == 3 ? 'Four' : 'Three'}-Week Look Ahead.`}
      </Text>
    </Box>
  );
};

export interface PlanProps {
  expiredMilestoneStats?: MutableRefObject<CrewMemberExpiredMilestoneStats>;
}

export const Plan: React.FC<PlanProps> = ({ expiredMilestoneStats }) => {
  const { numPlanningNextWeeks } = useConfig();
  const { selectedWpList, selectedForeman, planningDispatch } = usePlanning();
  const debouncedSearchQuery = useDebounce(selectedWpList, 500);
  const dispatchToast = useToastsDispatch();
  const [showForemanToast, setShowForemanToast] = useState<boolean>(true);
  const [aiPlannedMilestoneCount, setAiPlannedMilestoneCount] = useState(0);
  const [aiPlanStatusMessage, setAiPlanStatusMessage] = useState('');
  const [aiStatusMessageData, setAiStatusMessageData] = useState<any>(null);
  const [showAiSummaryModal, setShowAiSummaryModal] = useState(false);

  // Helper function to add foreman name to AI planning status message
  const formatMessageWithForemanName = useCallback((message: string, foreman?: CrewMember | null): string => {
    if (!message || !foreman) return message;
    // Convert to proper case, handling multi-word names: "VASQUEZ CASTELLANOS" -> "Vasquez Castellanos"
    const toProperCase = (str: string) =>
      str
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    const foremanName = `${toProperCase(foreman.firstName)} ${toProperCase(foreman.lastName)}`;
    // Insert foreman name after "Planning complete"
    return message.replace(/^Planning complete:/, `Planning complete for ${foremanName}:`);
  }, []);

  const foremanSelectToast: ToastAction = {
    type: 'ADD_TOAST',
    payload: {
      id: TOAST_ID_SELECT_FOREMAN,
      toastType: ToastType.INFO,
      content: () => foremanToast(numPlanningNextWeeks),
      alertAction: {
        label: 'GOT IT',
        onClickHandler: () => {
          noop;
        },
      },
    },
  };
  const [showFooter, setShowFooter] = useState(true);

  // Check if AI planning is active (blocks schedule edit and ownership edit)
  const isAiPlanningActive = useMemo(() => {
    return (
      selectedForeman?.planningAiRequest?.status === 'PROCESSING' ||
      (selectedForeman?.planningAiRequest?.status === 'COMPLETED' && aiPlannedMilestoneCount > 0)
    );
  }, [
    selectedForeman?.planningAiRequest?.status,
    selectedForeman?.planningAiRequest?.id,
    selectedForeman?.id,
    aiPlannedMilestoneCount,
  ]);

  const [loadWorkpackages, { loading, data, error, refetch, networkStatus }] = useLazyQuery<
    {
      workPackagesByIds: WorkPackage[];
    },
    {
      ids: string[];
    }
  >(GET_WORKPACKAGE_ADDITIONAL_INFO_BY_IDS);

  // Reset footer visibility when crew hierarchy changes (foreman/AI request changes)
  useEffect(() => {
    setShowFooter(true);
  }, [selectedForeman?.id, selectedForeman?.planningAiRequest?.id]);

  // Initialize state from selectedForeman.planningAiRequest when it changes
  useEffect(() => {
    if (selectedForeman?.planningAiRequest) {
      // Set count
      if (selectedForeman.planningAiRequest.count !== undefined) {
        setAiPlannedMilestoneCount(selectedForeman.planningAiRequest.count);
        // IMPORTANT: Also dispatch to context so other components can access it
        planningDispatch({
          type: PlanningActionType.SetAiPlannedMilestoneCount,
          payload: { aiPlannedMilestoneCount: selectedForeman.planningAiRequest.count },
        });
      } else {
        setAiPlannedMilestoneCount(0);
        planningDispatch({
          type: PlanningActionType.SetAiPlannedMilestoneCount,
          payload: { aiPlannedMilestoneCount: 0 },
        });
      }

      // Set statusMessageData
      const parsedStatusMessageData = parseStatusMessageData(selectedForeman.planningAiRequest.statusMessageData);
      if (parsedStatusMessageData) {
        setAiStatusMessageData(parsedStatusMessageData);
      } else {
        setAiStatusMessageData(null);
      }

      // Set planStatusMessage: use statusMessage from backend if available, otherwise parse from statusMessageData
      if (selectedForeman.planningAiRequest.statusMessage) {
        const formattedMessage = formatMessageWithForemanName(
          selectedForeman.planningAiRequest.statusMessage,
          selectedForeman
        );
        setAiPlanStatusMessage(formattedMessage);
      } else if (parsedStatusMessageData?.planStatus) {
        const formattedMessage = formatMessageWithForemanName(parsedStatusMessageData.planStatus, selectedForeman);
        setAiPlanStatusMessage(formattedMessage);
      } else {
        setAiPlanStatusMessage('');
      }
    } else {
      // Clear all state when there's no planningAiRequest
      setAiPlannedMilestoneCount(0);
      setAiStatusMessageData(null);
      setAiPlanStatusMessage('');
      // Dispatch to context as well
      planningDispatch({
        type: PlanningActionType.SetAiPlannedMilestoneCount,
        payload: { aiPlannedMilestoneCount: 0 },
      });
    }
  }, [
    selectedForeman?.planningAiRequest?.id,
    selectedForeman?.planningAiRequest?.status,
    selectedForeman?.planningAiRequest?.count,
    selectedForeman?.planningAiRequest?.statusMessageData,
    selectedForeman?.planningAiRequest?.statusMessage,
    selectedForeman?.firstName,
    selectedForeman?.lastName,
    planningDispatch,
    formatMessageWithForemanName,
  ]);

  useEffect(() => {
    if (!selectedWpList || selectedWpList.length < 1) return;
    loadWorkpackages({
      variables: {
        ids: selectedWpList,
      },
    });
  }, [debouncedSearchQuery, selectedWpList]);

  useEffect(() => {
    if (showForemanToast) {
      dispatchToast(foremanSelectToast);
      setShowForemanToast(!showForemanToast);
    }
    if (selectedForeman && !(selectedForeman.id == AllForemanId || selectedForeman.id == UnassignedForemanId)) {
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: foremanSelectToast.payload.id } });
    }
  }, [selectedForeman, dispatchToast, showForemanToast, foremanSelectToast]);

  const workPackageDetails: WorkPackage[] = useMemo(() => {
    if (!selectedWpList || selectedWpList.length < 1) return [];
    return get(data, 'workPackagesByIds', []);
  }, [data, selectedWpList]);

  const planningAiRequestId = selectedForeman?.planningAiRequest?.id || null;
  const planningAiRequestStatus = selectedForeman?.planningAiRequest?.status;

  const handleAiPlannedMilestoneCount = useCallback(
    (count: number, message?: string) => {
      setAiPlannedMilestoneCount(count);
      if (message) {
        const formattedMessage = formatMessageWithForemanName(message, selectedForeman);
        setAiPlanStatusMessage(formattedMessage);
      }
    },
    [formatMessageWithForemanName, selectedForeman]
  );

  const handleFooterClose = useCallback(() => setShowFooter(false), []);

  // Memoize footer visibility calculation
  // Show footer when status is COMPLETED (AI has finished planning, waiting for user to Save/Cancel)
  const shouldShowFooter = useMemo(() => {
    return !!(planningAiRequestId && showFooter && planningAiRequestStatus === 'COMPLETED');
  }, [planningAiRequestId, showFooter, planningAiRequestStatus]);

  // Remove any in-progress AI planning toast when footer appears to avoid overlap
  useEffect(() => {
    if (shouldShowFooter) {
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-planning-success' } });
    }
  }, [shouldShowFooter, dispatchToast]);

  return (
    <>
      <PlanningActionContainer data-testid="planning-actions">
        <PlanningActions
          workPackages={workPackageDetails}
          loading={loading || networkStatus === NetworkStatus.refetch}
          isAiPlanningActive={isAiPlanningActive}
        />
      </PlanningActionContainer>
      {(loading || networkStatus === NetworkStatus.refetch) && (
        <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }} p={{ sm: '500' }} data-testid="loading">
          <Spinner size={SpinnerSize.MEDIUM} />
        </Box>
      )}
      {!loading && error && (
        <Box data-testid="retry">
          <ErrorRetry
            refetch={() =>
              refetch({
                ids: selectedWpList && selectedWpList.length > 0 ? selectedWpList : [],
              })
            }
          >
            <Trans>There was a problem loading header content. Please click the button below to try again.</Trans>
          </ErrorRetry>
        </Box>
      )}
      {!loading && !error && (
        <WorkPackageDetailHeader
          isPlanningModule={true}
          showSecondaryOwners={false}
          workPackages={workPackageDetails}
          disableScheduleEdit={isAiPlanningActive}
        />
      )}
      <PlanningViewDnd
        expiredMilestoneStats={expiredMilestoneStats}
        aiStatusMessageData={aiStatusMessageData}
        setAiStatusMessageData={setAiStatusMessageData}
        showAiSummaryModal={showAiSummaryModal}
        setShowAiSummaryModal={setShowAiSummaryModal}
        onAiPlannedMilestoneCount={handleAiPlannedMilestoneCount}
      />
      {shouldShowFooter && planningAiRequestId && (
        <PlannedMilestoneFooterComponent
          aiPlannedMilestoneCount={aiPlannedMilestoneCount}
          aiPlanStatusMessage={aiPlanStatusMessage}
          planningAiRequestId={planningAiRequestId}
          aiStatusMessageData={aiStatusMessageData}
          showAiSummaryModal={showAiSummaryModal}
          setShowAiSummaryModal={setShowAiSummaryModal}
          onClose={handleFooterClose}
        />
      )}
    </>
  );
};

// Sticky footer component for planned milestones
interface PlannedMilestoneFooterComponentProps {
  aiPlannedMilestoneCount: number;
  aiPlanStatusMessage: string;
  planningAiRequestId: string;
  aiStatusMessageData: any;
  showAiSummaryModal: boolean;
  setShowAiSummaryModal: (show: boolean) => void;
  onClose: () => void;
}

const PlannedMilestoneFooterComponent: React.FC<PlannedMilestoneFooterComponentProps> = ({
  aiPlannedMilestoneCount,
  aiPlanStatusMessage,
  planningAiRequestId,
  aiStatusMessageData,
  showAiSummaryModal,
  setShowAiSummaryModal,
  onClose,
}) => {
  const { handleSaveAiPlanning, loading: saveLoading } = useSaveAiPlanningHandler();
  const { handleCancelAiPlanning, loading: cancelLoading } = useCancelAiPlanningHandler();
  const { selectedForeman, selectedGeneralForeman, selectedSuperintendent } = usePlanning();
  const { canManageAIPlanningAll, canManageAIPlanningAssigned, canManageAIPlanningArea } = usePlanningPermissions();
  const { user } = useAppUser();

  // Check if logged-in user can edit this AI plan
  // Requirements: 1) User must have AI planning permission, 2) User must be in the selected hierarchy
  const canEditAIPlan = React.useMemo(() => {
    // Must have AI planning permission (any scope)
    if (!canManageAIPlanningAll && !canManageAIPlanningAssigned && !canManageAIPlanningArea) return false;

    // User must be in the selected hierarchy (foreman/GF/superintendent)
    const userCrewMemberId = user?.crewMember?.id;
    if (!userCrewMemberId) return false;

    return (
      userCrewMemberId === selectedForeman?.id ||
      userCrewMemberId === selectedGeneralForeman?.id ||
      userCrewMemberId === selectedSuperintendent?.id
    );
  }, [
    canManageAIPlanningAll,
    canManageAIPlanningAssigned,
    canManageAIPlanningArea,
    user?.crewMember?.id,
    selectedForeman?.id,
    selectedGeneralForeman?.id,
    selectedSuperintendent?.id,
  ]);

  const handleSave = useCallback(async () => {
    const success = await handleSaveAiPlanning(planningAiRequestId);
    if (success) {
      onClose();
    }
  }, [handleSaveAiPlanning, planningAiRequestId, onClose]);

  const handleCancel = useCallback(async () => {
    const success = await handleCancelAiPlanning(planningAiRequestId);
    if (success) {
      onClose();
    }
  }, [handleCancelAiPlanning, planningAiRequestId, onClose]);

  const handleInfoIconClick = useCallback(() => {
    if (aiStatusMessageData) {
      setShowAiSummaryModal(true);
    }
  }, [aiStatusMessageData, setShowAiSummaryModal]);

  const handleSummaryModalClose = useCallback(() => {
    setShowAiSummaryModal(false);
  }, [setShowAiSummaryModal]);

  return (
    <>
      {showAiSummaryModal && (
        <AiPlanningSummaryModal
          visible={showAiSummaryModal}
          statusMessageData={aiStatusMessageData}
          onClose={handleSummaryModalClose}
        />
      )}
      <PlannedMilestoneFooter data-ai-footer-visible="true">
        <PlannedMilestoneFooterText>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              cursor: aiStatusMessageData ? 'pointer' : 'default',
              marginRight: '8px',
            }}
            onClick={handleInfoIconClick}
          >
            <Icon icon="info-circle" size={IconSize.MEDIUM} />
          </span>
          {aiPlanStatusMessage ||
            `${aiPlannedMilestoneCount} milestones are planned. Please verify before you save the changes.`}
        </PlannedMilestoneFooterText>
        <PlannedMilestoneFooterActions>
          {aiPlannedMilestoneCount ? (
            <>
              <Button
                appearance={ButtonAppearance.SECONDARY}
                size={ButtonSize.MEDIUM}
                onClick={handleCancel}
                disabled={!canEditAIPlan || cancelLoading || saveLoading}
                data-testid="cancel-ai-planning-btn"
              >
                {cancelLoading ? 'Cancelling...' : 'Cancel'}
              </Button>
              <Button
                appearance={ButtonAppearance.PRIMARY}
                size={ButtonSize.MEDIUM}
                onClick={handleSave}
                disabled={!canEditAIPlan || saveLoading || cancelLoading}
                data-testid="save-ai-planning-btn"
              >
                {saveLoading ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            <Box
              onClick={onClose}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              data-testid="close-footer-btn"
            >
              <Icon icon="times" size={IconSize.MEDIUM} />
            </Box>
          )}
        </PlannedMilestoneFooterActions>
      </PlannedMilestoneFooter>
    </>
  );
};
